/* eslint-disable import/named */
import { get } from 'lodash';
import {
    ActivityEvent,
    BusinessEvent,
    Click,
    Hover,
    Any,
    Impression,
    Locale,
    Metadata,
    Page,
    PageView,
    Platform,
    User
} from '@fiverr-private/bianka_js';
import { BIANKA_ACTIVITY_TYPES } from '../activityTypes';

const ACTIVITY_TYPE_TO_EVENT_MAP = {
    [BIANKA_ACTIVITY_TYPES.CLICK]: Click,
    [BIANKA_ACTIVITY_TYPES.HOVER]: Hover,
    [BIANKA_ACTIVITY_TYPES.PAGE_VIEW]: PageView,
    [BIANKA_ACTIVITY_TYPES.IMPRESSION]: Impression
};

/**
 * Formats a given Request into a ActivityEvent entity.
 * @param {ReportActivityOptions} options
 * @return {ActivityEvent}
 */
export const formatActivityRequest = ({
    event,
    type,
    uid,
    contextId,
    page,
    user,
    locale,
    additionalData,
    hostname,
    testing = false,
    metadataId
}) => {
    let protoType = type;
    if (typeof type === 'string') {
        protoType = ACTIVITY_TYPE_TO_EVENT_MAP[type];

        if (!protoType) {
            throw new Error(
                `Invalid activity event type: ${type}. Possible activity types are:
             ${Object.values(ACTIVITY_TYPE_TO_EVENT_MAP)}`
            );
        }
    }

    const eventMessage = protoType.create(event);
    const activityEvent = {
        metadata: formatMetadata({ testing, hostname, contextId, metadataId }),
        page,
        uid,
        platform: Platform.UNSPECIFIED,
        event: Any.pack(eventMessage, protoType)
    };

    if (user) {
        activityEvent.user = User.create(user);
    }

    if (page) {
        activityEvent.page = Page.create(page);
    }

    if (locale) {
        activityEvent.locale = Locale.create(locale);
    }

    if (additionalData) {
        const additionalDataMessage = additionalData.type.create(additionalData.message);
        activityEvent.additionalData = Any.pack(additionalDataMessage, additionalData.type);
    }

    return ActivityEvent.create(activityEvent);
};

/**
 * Formats a given Request into a BusinessEvent entity.
 * @param {ReportBusinessOptions} options
 * @return {BusinessEvent}
 */
export const formatBusinessRequest = ({
    event,
    type,
    uid,
    contextId,
    hostname,
    testing = false,
    metadataId
}) => {
    const eventMessage = type.create(event);
    const businessEvent = {
        metadata: formatMetadata({ testing, hostname, contextId, metadataId }),
        platform: Platform.UNSPECIFIED,
        uid,
        event: Any.pack(eventMessage, type)
    };

    return BusinessEvent.create(businessEvent);
};

/**
 * Formats a given event type into a metric name
 * @param {string|MessageType<T>}type
 * @return {string}
 */
export const formatEventMetricName = (type) => {
    if (typeof type === 'string') {
        return type;
    }

    const [typeName] = get(type, 'typeName', '').split('.').slice(-1) || [];

    return typeName || 'unknown';
};

/**
 * Formats request's Metadata object.
 * @param testing
 * @param hostname
 * @param contextId
 * @param metadataId
 * @return {Metadata}
 */
export const formatMetadata = ({ testing, hostname, contextId, metadataId }) => Metadata.create({
    id: metadataId,
    contextId,
    prod: !testing,
    createdAt: Date.now(),
    hostname
});
